// ============================================================
// Teacher Store (Zustand)
// ============================================================

import { create } from 'zustand';
import teacherService from '@/services/teacherService';

const useTeacherStore = create((set, get) => ({
  teachers: [],
  currentTeacher: null,
  
  // Teacher Workspace State
  dashboard: null,
  assignments: [],
  homework: [],
  classwork: [],
  timetable: [],
  notifications: [],
  
  // UI State
  loading: false,
  dashboardLoading: false,
  error: null,
  loadedSchoolId: null,

  // ── Management Actions ─────────────────────────────────────

  fetchTeachers: async (schoolId, forceRefetch = false) => {
    const state = get();
    if (!forceRefetch && state.loadedSchoolId === schoolId && state.teachers.length > 0) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const res = await teacherService.getTeachers(schoolId);
      set({ teachers: res.data, loadedSchoolId: schoolId, loading: false });
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  fetchTeacherById: async (schoolId, teacherId) => {
    set({ loading: true, error: null, currentTeacher: null });
    try {
      const res = await teacherService.getTeacherById(schoolId, teacherId);
      set({ currentTeacher: res.data, loading: false });
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  createTeacher: async (schoolId, data) => {
    set({ loading: true, error: null });
    try {
      const res = await teacherService.createTeacher(schoolId, data);
      set((state) => ({
        teachers: [res.data, ...state.teachers],
        loading: false,
      }));
      return { success: true, data: res.data };
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  updateTeacher: async (schoolId, teacherId, data) => {
    set({ loading: true, error: null });
    try {
      const res = await teacherService.updateTeacher(schoolId, teacherId, data);
      set((state) => ({
        teachers: state.teachers.map((t) => (t.id === teacherId ? res.data : t)),
        currentTeacher: state.currentTeacher?.id === teacherId ? res.data : state.currentTeacher,
        loading: false,
      }));
      return { success: true, data: res.data };
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  deleteTeacher: async (schoolId, teacherId) => {
    set({ loading: true, error: null });
    try {
      await teacherService.deleteTeacher(schoolId, teacherId);
      set((state) => ({
        teachers: state.teachers.map((t) => (t.id === teacherId ? { ...t, status: 'inactive' } : t)),
        loading: false,
      }));
      return { success: true };
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  // ── Workspace Actions ──────────────────────────────────────

  fetchTeacherDashboard: async (teacherId) => {
    set({ dashboardLoading: true, error: null });
    try {
      const res = await teacherService.getTeacherDashboard(teacherId);
      set({ dashboard: res.data, dashboardLoading: false });
    } catch (err) {
      set({ error: err.message, dashboardLoading: false });
    }
  },

  fetchTeacherAssignments: async (schoolId, teacherId) => {
    set({ loading: true, error: null });
    try {
      const res = await teacherService.getTeacherAssignments(schoolId, teacherId);
      set({ assignments: res.data, loading: false });
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  fetchTeacherNotifications: async (teacherId) => {
    set({ loading: true, error: null });
    try {
      const res = await teacherService.getTeacherNotifications(teacherId);
      set({ notifications: res.data, loading: false });
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  fetchTeacherHomework: async (schoolId, teacherId) => {
    set({ loading: true, error: null });
    try {
      const res = await teacherService.getTeacherHomework(schoolId, teacherId);
      set({ homework: res.data, loading: false });
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  fetchTeacherTimetable: async (teacherId) => {
    set({ loading: true, error: null });
    try {
      const res = await teacherService.getTeacherTimetable(teacherId);
      set({ timetable: res.data, loading: false });
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  fetchTeacherClasswork: async (schoolId, teacherId, date) => {
    set({ loading: true, error: null });
    try {
      const res = await teacherService.getTeacherClasswork(schoolId, teacherId, date);
      set({ classwork: res.data, loading: false });
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  clearCurrentTeacher: () => set({ currentTeacher: null }),
}));

export default useTeacherStore;
